import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Vazoul-branded Sonner toaster.
 * - Glassmorphism card with gradient border accent
 * - Colored left rail per intent (success / error / warning / info)
 * - Mono-tinged title, muted description — matches Bloomberg-style terminal aesthetic
 * - Animated icon container with subtle glow
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="top-right"
      expand
      richColors={false}
      toastOptions={{
        unstyled: false,
        classNames: {
          toast: [
            "group toast pointer-events-auto relative flex w-full items-start gap-3",
            "overflow-hidden rounded-xl border border-border/50",
            "bg-card/85 backdrop-blur-2xl",
            "px-4 py-3.5 pr-10",
            "shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.25),0_2px_8px_-2px_hsl(0_0%_0%/0.15)]",
            "before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-primary before:rounded-l-xl",
            "after:absolute after:inset-x-0 after:top-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-primary/40 after:to-transparent",
            "data-[type=success]:before:bg-success",
            "data-[type=error]:before:bg-destructive",
            "data-[type=warning]:before:bg-warning",
            "data-[type=info]:before:bg-primary",
          ].join(" "),
          title:
            "text-sm font-semibold tracking-tight text-foreground leading-snug",
          description:
            "text-xs text-muted-foreground leading-relaxed mt-0.5 font-mono",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:rounded-md group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs group-[.toast]:font-semibold group-[.toast]:hover:bg-primary/90 group-[.toast]:transition-colors",
          cancelButton:
            "group-[.toast]:bg-muted/60 group-[.toast]:text-muted-foreground group-[.toast]:rounded-md group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs group-[.toast]:hover:bg-muted",
          closeButton:
            "group-[.toast]:!bg-background/80 group-[.toast]:!border-border/60 group-[.toast]:!text-muted-foreground group-[.toast]:hover:!text-foreground group-[.toast]:!backdrop-blur-sm group-[.toast]:!transition-colors",
          icon: [
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            "bg-gradient-to-br from-primary/15 to-primary/5",
            "border border-primary/20",
            "shadow-[0_0_16px_-4px_hsl(var(--primary)/0.4)]",
            "group-data-[type=success]:from-success/15 group-data-[type=success]:to-success/5 group-data-[type=success]:border-success/25 group-data-[type=success]:shadow-[0_0_16px_-4px_hsl(var(--success)/0.45)] group-data-[type=success]:text-success",
            "group-data-[type=error]:from-destructive/15 group-data-[type=error]:to-destructive/5 group-data-[type=error]:border-destructive/25 group-data-[type=error]:shadow-[0_0_16px_-4px_hsl(var(--destructive)/0.45)] group-data-[type=error]:text-destructive",
            "group-data-[type=warning]:from-warning/15 group-data-[type=warning]:to-warning/5 group-data-[type=warning]:border-warning/25 group-data-[type=warning]:shadow-[0_0_16px_-4px_hsl(var(--warning)/0.45)] group-data-[type=warning]:text-warning",
            "group-data-[type=info]:text-primary",
          ].join(" "),
          success: "data-[type=success]",
          error: "data-[type=error]",
          warning: "data-[type=warning]",
          info: "data-[type=info]",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
